import { Injectable } from '@angular/core';
import { groupBy } from 'lodash';
import { jobBehavioralRangesValidation } from '../constants-types/constants';
import {
    ExternalAssessmentStates,
    Impression,
    JobTargetCreationMethod,
    ScoreInfoType,
    SurveyUserTypes
} from '../constants-types/Enums';
import { ABCD } from '../core-data-model/BehavioralFactor.model';
import { BehavioralFactorRanges } from '../core-data-model/BehavioralFactorRanges.model';
import { BenchmarkOccupation } from '../core-data-model/BenchmarkOccupation.model';
import { ProfileBehavioralInfo } from '../core-data-model/BenchmarkProfile.model';
import { BenchmarkProfileServerEntry } from '../core-data-model/BenchmarkProfileApi.serverinterface';
import { CognitiveAssessment } from '../core-data-model/CognitiveAssessment.model';
import { CognitiveJobAssessment } from '../core-data-model/CognitiveJobAssessment.model';
import { CognitiveTargetQuestion } from '../core-data-model/CognitiveTargetQuestion.model';
import { MinMax } from '../core-data-model/Common.model';
import {
    ActiveJobEntry,
    ActiveJobEntryJob
} from '../core-data-model/Job/ActiveJobCardData.model';
import { JobPipelineHealth } from '../core-data-model/Job/JobPipelineHealth.model';
import { JobReportInfo } from '../core-data-model/Job/JobReportInfo.model';
import {
    Job,
    JobAssessmentLiteCreate,
    JobUpdate
} from '../core-data-model/Job.model';
import {
    ActiveJobsCardDataServerEntry,
    AssociatedPersonsServer,
    CognitiveJobAssessmentServer,
    CognitiveQuestionServer,
    CreateJobAssessmentPost,
    CreateJobAssessmentServer,
    JobAssessmentServer,
    JobAssociatedPersonServer,
    JobReportServer,
    JobServer,
    JobServerV3,
    ScoreJobCognitiveTargetPost,
    ScoreTargetFactorsPost,
    ScoreTargetFactorsServer
} from '../core-data-model/JobApi.serverinterface';
import { JobAssessment } from '../core-data-model/JobAssessment.model';
import { JobChecklistQuestion } from '../core-data-model/JobChecklistQuestion.model';
import { FullPattern } from '../core-data-model/Pattern.model';
import { JobFit, Person } from '../core-data-model/Person.model';
import { ReferencePatternsInformation } from '../core-data-model/ReferencePatternType.model';
import { JobReportEmailConfiguration } from '../core-data-model/ReportEmailConfiguration.model';
import { SigmaScore } from '../core-data-model/SigmaScore.interface';
import { BenchmarkProfileTransformers } from '../core-services/BenchmarkProfileApi.datatransform';
import { FoldersDataService } from '../core-services/Folders.dataservice';
import { JobPipelineTransformers } from '../core-services/JobPipeline.datatransform';
import { PatternTransformer } from '../core-services/Pattern.datatransform';
import { PersonApiTransformer } from '../core-services/PersonApi.datatransform';
import { ToolboxApi } from '../core-services/Toolbox.connect';
import { Util } from '../core-utilities/Util';
import { BehavioralAssessmentFactory } from '../object-factory/BehavioralAssessment.factory';
import { InitDataService } from '../system-services/InitData/InitData.service';
import { TranslationService } from '../translations';

interface IJobApiPayLoad {
    name: string;
    description: string;
    companyid: string;
    // listed as number in swagger but is number
    createdBy: string;
    onetSocCode?: string;
    folderId: string;
    hireVersion: string;
    targetFactorRanges: {
        aSigmaRangeLow: number;
        aSigmaRangeHigh: number;
        bSigmaRangeLow: number;
        bSigmaRangeHigh: number;
        cSigmaRangeLow: number;
        cSigmaRangeHigh: number;
        dSigmaRangeLow: number;
        dSigmaRangeHigh: number;
    };
    jobAssessmentLiteCreate?: JobAssessmentLiteCreate;
}

@Injectable({
    providedIn: 'root'
})
export class JobTransformers {
    constructor(
        private TR: TranslationService,
        private util: Util,
        private baFactory: BehavioralAssessmentFactory,
        private personTransformer: PersonApiTransformer,
        private jobPipelineTransformer: JobPipelineTransformers,
        private toolboxApi: ToolboxApi,
        private initData: InitDataService,
        private foldersService: FoldersDataService,
        private benchmarkProfileTransformers: BenchmarkProfileTransformers
    ) {}

    transformClientToServer(
        jobUpdate: JobUpdate,
        companyId: string,
        createdById: string
    ) {
        const serverJob: IJobApiPayLoad = {
            name: jobUpdate.title,
            companyid: companyId,
            createdBy: createdById,
            description: jobUpdate.description,
            folderId: jobUpdate.folder.id,
            hireVersion: jobUpdate.hireVersion,
            targetFactorRanges: {
                aSigmaRangeHigh: jobUpdate.behavioralRanges
                    ? jobUpdate.behavioralRanges.A.max
                    : 0,
                aSigmaRangeLow: jobUpdate.behavioralRanges
                    ? jobUpdate.behavioralRanges.A.min
                    : 0,
                bSigmaRangeHigh: jobUpdate.behavioralRanges
                    ? jobUpdate.behavioralRanges.B.max
                    : 0,
                bSigmaRangeLow: jobUpdate.behavioralRanges
                    ? jobUpdate.behavioralRanges.B.min
                    : 0,
                cSigmaRangeHigh: jobUpdate.behavioralRanges
                    ? jobUpdate.behavioralRanges.C.max
                    : 0,
                cSigmaRangeLow: jobUpdate.behavioralRanges
                    ? jobUpdate.behavioralRanges.C.min
                    : 0,
                dSigmaRangeHigh: jobUpdate.behavioralRanges
                    ? jobUpdate.behavioralRanges.D.max
                    : 0,
                dSigmaRangeLow: jobUpdate.behavioralRanges
                    ? jobUpdate.behavioralRanges.D.min
                    : 0
            }
        };
        if (jobUpdate.benchmarkOccupation) {
            serverJob.onetSocCode = jobUpdate.benchmarkOccupation.id;
        }
        if (jobUpdate.jobAssesmentLiteCreate) {
            serverJob.jobAssessmentLiteCreate =
                jobUpdate.jobAssesmentLiteCreate;
        }
        return serverJob;
    }

    transform(jobJSON: JobServer): Job {
        const job = new Job({
            id: jobJSON.jobId,
            benchmarkOccupation: new BenchmarkOccupation({
                id: jobJSON.onetSocCode
            }),
            title: jobJSON.name,
            description: jobJSON.description,
            companyId: jobJSON.companyId,
            createdByName: jobJSON.createdByName, // should this be id
            createdDate: this.util.newDate(jobJSON.createdOn),
            modifiedDate: this.util.newDate(jobJSON.updatedOn),
            lastModifiedBy: jobJSON.updatedByName,
            behavioralRanges: this.transformBehavioralJobScore(
                jobJSON.targetFactorRanges
            ),
            cognitiveTarget: jobJSON.cognitiveTarget,
            onetSocCode: jobJSON.onetSocCode,
            folder: this.foldersService.getFolder(jobJSON.folderId),
            // TODO this is replicated by above
            lastUpdated: this.util.newDate(jobJSON.updatedOn),
            jobPipeline: null,
            cognitiveWeight: jobJSON.cognitiveWeight,
            cognitiveBaselineScaledScore: jobJSON.baseCognitiveScaledScore,
            benchmarkProfiles: this.transformBenchmarkProfiles(
                jobJSON.benchmarkProfiles
            ),
            isOwner: jobJSON.isOwner
        });

        return job;
    }

    // NOTE: this is static because the imageservice needs to call it
    // and this service has dependencies that can't be imported in the imageservice (see: InitDataService)
    static transformToJobReport(
        jobReportJSON: JobReportServer,
        id?: string
    ): JobReportInfo {
        const jobReport = new JobReportInfo({
            id: id,
            jobTitle: jobReportJSON.jobTitle,
            jobDescription: jobReportJSON.jobDescription,
            aSigmaRangeLow: jobReportJSON.aSigmaRangeLow,
            aSigmaRangeHigh: jobReportJSON.aSigmaRangeHigh,
            bSigmaRangeLow: jobReportJSON.bSigmaRangeLow,
            bSigmaRangeHigh: jobReportJSON.bSigmaRangeHigh,
            cSigmaRangeLow: jobReportJSON.cSigmaRangeLow,
            cSigmaRangeHigh: jobReportJSON.cSigmaRangeHigh,
            dSigmaRangeLow: jobReportJSON.dSigmaRangeLow,
            dSigmaRangeHigh: jobReportJSON.dSigmaRangeHigh,
            matchingReferenceProfiles:
                jobReportJSON.matchingReferenceProfiles.map((mp) => ({
                    name: mp.name,
                    badgeUrl: mp.badgeUrl,
                    percent: null,
                    behavioralInfo: {
                        description: mp.description,
                        moreInfoLink: mp.moreInfoLink
                    } as ProfileBehavioralInfo
                })),
            expectedBehaviors: jobReportJSON.expectedBehaviors,
            motivatingNeeds: jobReportJSON.motivatingNeeds,
            appealingWords: jobReportJSON.appealingWords,
            primaryBehaviorTrait1: jobReportJSON.primaryBehaviorTrait1,
            primaryBehaviorTrait2: jobReportJSON.primaryBehaviorTrait2,
            noteworthyBehaviors1: jobReportJSON.noteworthyBehaviors1,
            noteworthyBehaviors2: jobReportJSON.noteworthyBehaviors2,
            cogScaledScore: jobReportJSON.cogScaledScore,
            cogWeight: jobReportJSON.cogWeight,
            hasCogAccess: jobReportJSON.hasCogAccess,
            mainReportTexts: jobReportJSON.mainReportTexts,
            reportCharacteristics: jobReportJSON.reportCharacteristics,
            reportAccompanyingTexts: jobReportJSON.reportAccompanyingTexts
        });

        return jobReport;
    }

    transformToJobReport(
        jobReportJSON: JobReportServer,
        id?: string
    ): JobReportInfo {
        return JobTransformers.transformToJobReport(jobReportJSON, id);
    }

    private transformBenchmarkProfiles(
        benchmarkProfilesJSON: BenchmarkProfileServerEntry[]
    ) {
        const benchmarkProfiles = [];

        if (benchmarkProfilesJSON && benchmarkProfilesJSON.length > 0) {
            for (const benchmarkProfileJson of benchmarkProfilesJSON) {
                benchmarkProfiles.push(
                    this.benchmarkProfileTransformers.transformFromServer(
                        benchmarkProfileJson
                    )
                );
            }
        }

        return benchmarkProfiles;
    }

    transformPersonMatches(jobMatchJSON: JobAssociatedPersonServer): Person {
        return this.personTransformer.transformPerson(
            jobMatchJSON.person,
            new JobFit({
                personImpression: jobMatchJSON.person.assessmentUser.impression,
                overallFitScore: jobMatchJSON.combinedFitScore,
                behavioralFitScore: jobMatchJSON.behavioralFitScore,
                cognitiveFitScore: jobMatchJSON.cognitiveFitScore
            })
        );
    }

    transformPipelineHealth(
        jobId: string,
        jobMatchJSON: AssociatedPersonsServer
    ): JobPipelineHealth {
        return this.jobPipelineTransformer.transformPipelineHealth(
            jobId,
            jobMatchJSON.jobAssociatedPeople
        );
    }

    transformCognitiveQuestion(
        question: CognitiveQuestionServer
    ): CognitiveTargetQuestion {
        return {
            id: question.skillId,
            title: question.skillName,
            description: question.skillDescription,
            responses: [
                question.basicDescription,
                question.intermediateDescription,
                question.advancedDescription,
                question.noneDescription
            ],
            responseSelected: null
            // FOR TESTING DO NOT COMMIT UNCOMMENTED
            // responseSelected: 0
        };
    }

    transformBehavioralJobScore(
        scoreTargetFactors: ScoreTargetFactorsServer | SigmaScore
    ): BehavioralFactorRanges {
        if (this.util.hasValue(scoreTargetFactors)) {
            const retval = new BehavioralFactorRanges();
            ABCD.forEach((factor) => {
                const indexVariantHigh =
                    scoreTargetFactors[`position${factor}SigmaRangeHigh`];
                const indexVariantLow =
                    scoreTargetFactors[`position${factor}SigmaRangeLow`];
                const apiVariantHigh =
                    scoreTargetFactors[`${factor.toLowerCase()}SigmaRangeHigh`];
                const apiVariantLow =
                    scoreTargetFactors[`${factor.toLowerCase()}SigmaRangeLow`];
                retval[factor] = new MinMax(
                    {
                        min: indexVariantLow || apiVariantLow || 0,
                        max: indexVariantHigh || apiVariantHigh || 0
                    },
                    jobBehavioralRangesValidation
                );
            });
            // There is a weird server behavior where a empty result comes back as all 0's
            // This is here to handle that case. When that case goes away, this could be
            // removed.
            if (
                ABCD.every(
                    (factor) =>
                        retval[factor].min === 0 && retval[factor].max === 0
                )
            ) {
                return null;
            }
            return retval;
        }
        return null;
    }

    transformBehavioralJobScorePost(
        questions: JobChecklistQuestion[]
    ): ScoreTargetFactorsPost {
        return questions.map((question) => ({
            questionNumber: question.id,
            isSelected: question.checked ? 1 : 0
        }));
    }

    transformChecklistQuestions(questions: string[]): JobChecklistQuestion[] {
        return questions.map((question, index) => ({
            // API expects 1-indexed not 0
            id: index + 1,
            text: question,
            checked: false
        }));
    }

    /**
     * This is done because we order the questions on the frontend as
     * [basic, intermediate, advanced, none], but the actual values are
     * [1, 2, 3, 0]
     *
     * @param cognitiveResponseSelected
     */
    private transformResponseNumber(cognitiveResponseSelected: number): number {
        return cognitiveResponseSelected === 3
            ? 0
            : cognitiveResponseSelected + 1;
    }

    transformCognitiveJobScorePost(
        questions: CognitiveTargetQuestion[]
    ): ScoreJobCognitiveTargetPost {
        return questions.map((question) => ({
            jobSkillQuestionNumber: question.id,
            jobSkillAnswerValue: this.transformResponseNumber(
                question.responseSelected
            )
        }));
    }

    transformBehavioralFactorRangesToServer(
        ranges: BehavioralFactorRanges
    ): ScoreTargetFactorsServer {
        return {
            aSigmaRangeLow: ranges.A.min,
            aSigmaRangeHigh: ranges.A.max,
            bSigmaRangeLow: ranges.B.min,
            bSigmaRangeHigh: ranges.B.max,
            cSigmaRangeLow: ranges.C.min,
            cSigmaRangeHigh: ranges.C.max,
            dSigmaRangeLow: ranges.D.min,
            dSigmaRangeHigh: ranges.D.max
        };
    }

    transformJobUpdateServerV3Patch(job: JobUpdate): JobServerV3 {
        const retval: JobServerV3 = {
            name: job.title,
            description: job.description,
            targetFactorRanges: job.behavioralRanges
                ? this.transformBehavioralFactorRangesToServer(
                      job.behavioralRanges
                  )
                : undefined,
            cognitiveTarget: job.cognitiveTarget,
            cognitiveWeight: job.cognitiveWeight,
            jobTargetCreationMethod:
                JobTargetCreationMethod[job.jobTargetCreationMethod]
        };
        if (job.folder) {
            retval.folderId = job.folder.id;
        }
        return retval;
    }

    transformCreateJobAssessmentPost(
        job: Job,
        sender: { email: string; firstName: string; lastName: string },
        recipient: { email: string; fullName: () => string },
        checklist?: JobChecklistQuestion[],
        sendEmail = true
    ): CreateJobAssessmentPost {
        const retval: CreateJobAssessmentPost = {
            email: recipient.email,
            administeredByEmail: sender.email,
            administeredByFirstName: sender.firstName,
            administeredByLastName: sender.lastName,
            position: {
                assessmentPositionGuid: job.id
            },
            name: recipient.fullName(),
            languageLocale: this.TR.getDefaultBehavioralReportLanguage().locale,
            notifyAssessmentAvailableUsingEmail: sendEmail ? 1 : 0,
            isFromBeta: 'true'
        };
        if (checklist) {
            retval.responses = checklist.map(
                (question: JobChecklistQuestion) => ({
                    questionNumber: question.id,
                    isSelected: question.checked
                })
            );
        }
        return retval;
    }

    transformCreateJobAssessmentServer(
        server: CreateJobAssessmentServer
    ): JobAssessment {
        return new JobAssessment({
            assessmentState: server.assessmentState,
            behavioralPattern: PatternTransformer.makePattern(
                server.jobSigmaScore,
                ScoreInfoType.Position
            ),
            completedDate: this.util.newDate(
                server.assessmentCompletedDateTime
            ),
            expirationDate: this.util.newDate(server.expirationDateTime),
            id: server.assessmentId,
            jobId: server.position.assessmentPositionId,
            recipientName: server.name,
            recipientEmail: server.email,
            sendDate: this.util.newDate(server.assessmentDateTime),
            isFromBeta: 'true'
        });
    }

    transformJobAssessmentsServer(
        server: JobAssessmentServer[],
        jobId: string
    ): JobAssessment[] {
        const groupedIntoSameEmail = Object.values(
            groupBy(server, (ja) => ja.email)
        );
        return groupedIntoSameEmail
            .map((jasWithSameEmail: JobAssessmentServer[]) => {
                // Prioritize showing completed assessments even if there are newer
                // sent assessments
                const completedJAs = jasWithSameEmail.filter(
                    (ja) =>
                        ja.assessmentState ===
                        ExternalAssessmentStates.Completed
                );
                const jasToChooseFrom = completedJAs.length
                    ? completedJAs
                    : jasWithSameEmail;
                return jasToChooseFrom.reduce(
                    (
                        lastValue: JobAssessmentServer,
                        currentValue: JobAssessmentServer
                    ) =>
                        new Date(lastValue.assessmentDateTime).getTime() >
                        new Date(currentValue.assessmentDateTime).getTime()
                            ? lastValue
                            : currentValue
                );
            })
            .map((jaServer) =>
                this.transformJobAssessmentServer(jaServer, jobId)
            );
    }

    transformCognitiveJobAssessmentsServer(
        server: CognitiveJobAssessmentServer[],
        job: Job
    ): CognitiveJobAssessment[] {
        const groupedIntoSameEmail = Object.values(
            groupBy(server, (ja) => ja.email)
        );
        return groupedIntoSameEmail
            .map((jasWithSameEmail: CognitiveJobAssessmentServer[]) => {
                // Prioritize showing completed assessments even if there are newer
                // sent assessments
                const completedJAs = jasWithSameEmail.filter(
                    (ja) =>
                        ja.assessmentState ===
                        ExternalAssessmentStates.Completed
                );
                const jasToChooseFrom = completedJAs.length
                    ? completedJAs
                    : jasWithSameEmail;
                return jasToChooseFrom.reduce(
                    (
                        lastValue: CognitiveJobAssessmentServer,
                        currentValue: CognitiveJobAssessmentServer
                    ) =>
                        new Date(lastValue.assessmentDateTime).getTime() >
                        new Date(currentValue.assessmentDateTime).getTime()
                            ? lastValue
                            : currentValue
                );
            })
            .map((jaServer) =>
                this.transformCognitiveJobAssessmentServer(jaServer, job)
            );
    }

    private transformJobAssessmentServer(
        server: JobAssessmentServer,
        jobId: string
    ): JobAssessment {
        return new JobAssessment({
            assessmentState: server.assessmentState,
            behavioralPattern: PatternTransformer.makePattern(
                server.jobSigmaScore,
                ScoreInfoType.Position
            ),
            completedDate: this.util.newDate(
                server.assessmentCompletedDateTime
            ),
            expirationDate: this.util.newDate(server.expirationDateTime),
            id: server.assessmentId,
            jobId: jobId,
            recipientName: server.userName,
            recipientEmail: server.email,
            sendDate: this.util.newDate(server.assessmentDateTime),
            cognitiveScore: server.cognitiveScore,
            cognitiveScaledScore: server.cognitiveScaledScore
        });
    }

    private transformCognitiveJobAssessmentServer(
        server: CognitiveJobAssessmentServer,
        job: Job
    ): CognitiveJobAssessment {
        return new CognitiveJobAssessment({
            assessmentState: server.assessmentState,
            cognitiveJobScore: server.cognitiveJobScore,
            cognitiveJobScale: server.cognitiveJobScale,
            cognitiveJobPercentileRank: server.cognitiveJobPercentileRank,
            completedDate: this.util.newDate(
                server.assessmentCompletedDateTime
            ),
            expirationDate: this.util.newDate(server.expirationDateTime),
            id: server.assessmentId,
            jobId: job.id,
            recipientName: server.userName,
            recipientEmail: server.email,
            sendDate: this.util.newDate(server.assessmentDateTime)
        });
    }

    transformToServerReportConfig(
        jobListToReportOn: string[],
        includeCognitive: boolean,
        languageLocale: string = this.TR.currentLocale,
        reportEmailConfig: JobReportEmailConfiguration
    ) {
        if (reportEmailConfig.shouldSendToMyself) {
            reportEmailConfig.othersEmailList.push(this.initData.user.email);
        }

        let emailToList = '';

        if (
            typeof reportEmailConfig.othersEmailList != 'undefined' &&
            reportEmailConfig.othersEmailList &&
            reportEmailConfig.othersEmailList.length > 0
        ) {
            emailToList = reportEmailConfig.othersEmailList.join(',');
            if (reportEmailConfig.contributorsList.length > 0) {
                emailToList =
                    emailToList +
                    ',' +
                    reportEmailConfig.contributorsList
                        .toString()
                        .replace(/\s/g, '');
            }
        } else if (reportEmailConfig.contributorsList.length > 0) {
            emailToList = reportEmailConfig.contributorsList
                .toString()
                .replace(/\s/g, '');
        }

        const resultConfig = {
            jobsToReportOn: jobListToReportOn,
            emailTo: emailToList,
            emailSubject: reportEmailConfig.subject,
            emailMessageBody: reportEmailConfig.message,
            reportLanguage: languageLocale,
            includeCognitiveTarget: includeCognitive
        };

        return resultConfig;
    }

    transformPeopleMatchesToPeople(matchResponse: any): Person[] {
        return matchResponse.matchingPeopleByMatchScore.map(
            (matchPerson: any) => {
                const searchResult = new Person({
                    id: matchPerson.assessmentUserId,
                    firstName: matchPerson.firstName,
                    middleName: matchPerson.middleName,
                    lastName: matchPerson.lastName,
                    email: matchPerson.email,
                    type: SurveyUserTypes[
                        SurveyUserTypes[
                            matchPerson.personType as keyof typeof SurveyUserTypes
                        ] as any
                    ] as any,
                    job: matchPerson.currentJobInfo
                        ? new Job({
                              id: matchPerson.currentJobInfo.currentJobId,
                              title: matchPerson.currentJobInfo.currentJobName,
                              createdByName: `${matchPerson.currentJobInfo.createdByInfo.firstName} ${matchPerson.currentJobInfo.createdByInfo.lastName}`
                          })
                        : null,
                    jobFit: new JobFit({
                        overallFitScore: matchPerson.combinedJobFitScore,
                        behavioralFitScore: matchPerson.behavioralJobFitScore,
                        cognitiveFitScore: matchPerson.cognitiveJobFitScore
                    }),
                    referencePattern: matchPerson.behavioralInfo
                        .referencePatternInformation
                        ? ReferencePatternsInformation.find(
                              (pat) =>
                                  pat.id ===
                                  matchPerson.behavioralInfo
                                      .referencePatternInformation.patternNumber
                          )
                        : null,
                    behavioralAssessments: this.util.hasValue(
                        matchPerson.behavioralInfo.selfSigmaScore
                    )
                        ? [
                              this.baFactory.new_BehavioralAssessment({
                                  id: matchPerson.behavioralInfo.id,
                                  pattern: new FullPattern({
                                      self: {
                                          A: matchPerson.behavioralInfo
                                              .selfSigmaScore.aSigma,
                                          B: matchPerson.behavioralInfo
                                              .selfSigmaScore.bSigma,
                                          C: matchPerson.behavioralInfo
                                              .selfSigmaScore.cSigma,
                                          D: matchPerson.behavioralInfo
                                              .selfSigmaScore.dSigma,
                                          E: matchPerson.behavioralInfo
                                              .selfSigmaScore.eSigma,
                                          M: matchPerson.behavioralInfo
                                              .selfSigmaScore.mScore
                                      }
                                  }),
                                  assessmentState:
                                      ExternalAssessmentStates.Completed
                              })
                          ]
                        : [],
                    cognitiveAssessments: this.util.hasValue(
                        matchPerson.cognitiveInfo
                    )
                        ? [
                              new CognitiveAssessment({
                                  id: 'Unknown',
                                  score: matchPerson.cognitiveInfo.score,
                                  assessmentState:
                                      ExternalAssessmentStates.Completed,
                                  completedDate:
                                      matchPerson.cognitiveInfo.surveyDate
                              })
                          ]
                        : []

                    // lastCompleted is a little unfortunate since it is technically duplicated
                    // data, but we get an abbreviated set of data for table display and want
                    // this data shown despite not having full info on assessments
                    // lastCompleted: Date;
                    // lastSent: Date;
                    // lastUpdated: Date;
                });
                return searchResult;
            }
        );
    }

    async transformActiveJobsData(
        serverEntries: ActiveJobsCardDataServerEntry[]
    ): Promise<ActiveJobEntry[]> {
        return Promise.all(
            serverEntries.map((s) => {
                const job = s.job;
                const people = s.persons || [];
                const referenceProfileIds = job.referenceProfileIds || [];

                // Wait for all of them at once to maximize parellelization
                // since calls for the same id will hit the cache
                return Promise.all(
                    referenceProfileIds.map((id) =>
                        this.toolboxApi.getReferenceProfileById(id)
                    )
                ).then((jobReferenceProfiles) => ({
                    job: new ActiveJobEntryJob({
                        id: job.id,
                        title: job.name,
                        createdDate: this.util.newDate(job.createdDate),
                        jobPipeline:
                            this.jobPipelineTransformer.transformPipelineHealth(
                                job.id,
                                people
                            ),
                        matchingProfiles: jobReferenceProfiles
                    }),
                    associatedPeople: people.map((p) => ({
                        id: p.id,
                        firstName: p.firstName,
                        lastName: p.lastName,
                        lastCompleted: this.util.newDate(p.lastCompleted),
                        referencePattern: ReferencePatternsInformation.find(
                            (r) => r.id === p.referenceProfileId
                        ),
                        overallFitScore: p.overallFitScore,
                        personImpression: p.impression as Impression,
                        hasPendingBehavioral: p.hasPendingBehavioral,
                        hasPendingCognitive: p.hasPendingCognitive
                    }))
                }));
            })
        );
    }
}
